<template>
  <v-row :class="{ mobilePadding: isMobileBreakpoint }" no-gutters>
    <v-col cols="12">
      <v-form ref="form" v-model="valid" v-on:submit.prevent>
        <v-col cols="12" class="mb-0 mb-md-6">
          <v-row no-gutters align="center" class="pa-0 pa-md-2">
            <v-col cols="12" md="6" class="d-flex flex-column">
              <span class="body-regular-small title-text-color mb-2 mb-md-0">{{
                $t(i18nPath + 'playlistName.title')
              }}</span>
              <span
                class="body-regular-smaller desc-text-color d-none d-md-block"
                >{{ $t(i18nPath + 'playlistName.desc') }}</span
              >
            </v-col>
            <v-col cols="12" md="6" class="mt-0 mt-md-5">
              <base-input
                v-model="playlistData.name"
                class="playlist-text-input"
                :class="{ 'mobile-margin-bottom': isMobileBreakpoint }"
                maxLength="250"
                color="primary"
                :autofocus="!isMobileBreakpoint"
                outlined
                dense
                :rules="[rules.maxLength, rules.minLength]"
              ></base-input>
            </v-col>
          </v-row>
        </v-col>
        <!-- desc -->
        <v-col cols="12" class="mb-0 mb-md-6">
          <v-row no-gutters align="center" class="pa-0 pa-md-2">
            <v-col cols="12" md="6" class="d-flex flex-column">
              <span class="body-regular-small title-text-color mb-2 mb-md-0">{{
                $t(i18nPath + 'playlistDesc.title')
              }}</span>
              <span
                class="body-regular-smaller desc-text-color d-none d-md-block"
                >{{ $t(i18nPath + 'playlistDesc.desc') }}</span
              >
            </v-col>
            <v-col cols="12" md="6">
              <base-textarea
                v-model="playlistData.description"
                class="playlist-text-input mb-0 mb-md-6"
                :class="{ 'mobile-margin-bottom': isMobileBreakpoint }"
                :counter="
                  (isMobileBreakpoint ||
                    playlistData.description.length >= 5000) &&
                  5000
                "
                color="primary"
                outlined
                dense
                :rules="[rules.maxLengthDescription]"
                :messages="
                  playlistData.description.length === 5000 && [
                    $t('message.playlist.modal.rules.maxLengthDesc'),
                  ]
                "
              ></base-textarea>
            </v-col>
          </v-row>
        </v-col>
        <!-- access -->
        <v-col cols="12" class="mb-0 mb-md-6">
          <v-row no-gutters align="center" class="pa-0 pa-md-2">
            <v-col cols="12" md="6" class="d-flex flex-column">
              <span class="body-regular-small title-text-color mb-2 mb-md-0">{{
                $t(i18nPath + 'playlistAccess.title')
              }}</span>
              <span
                class="body-regular-smaller desc-text-color d-none d-md-block"
                >{{ $t(i18nPath + 'playlistAccess.desc') }}</span
              >
            </v-col>
            <v-col cols="12" md="6" style="position: relative">
              <v-select
                v-model="playlistData.access_type"
                :items="accessTypeItems"
                style="position: relative"
                :menu-props="{
                  'content-class': 'access-select-menu',
                  maxWitdh: 280,
                  offsetY: true,
                  closeOnContentClick: true,
                }"
                append-icon="mdi-chevron-down"
                class="access-select"
                :persistent-placeholder="true"
                outlined
                hide-details
                @input="handlePasswordInputFocus($event)"
              >
                <template #item="{ item }">
                  <v-list-item
                    class="d-flex access-options-list"
                    @click="playlistData.access_type = item.value"
                  >
                    <v-icon
                      v-if="item.value === playlistData.access_type"
                      size="16"
                      class="neutral-30-base--text mr-2 align-self-start"
                      >mdi-check</v-icon
                    >
                    <v-icon
                      v-else
                      style="visibility: hidden"
                      size="16"
                      class="neutral-30-base--text mr-2 align-self-start"
                      >mdi-check</v-icon
                    >
                    <div class="d-flex flex-column">
                      <v-list-item-title>
                        <span
                          class="access-option-title-text-color body-regular-smaller"
                        >
                          {{ item.name }}
                        </span>
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <span
                          class="access-option-subtitle-text-color body-regular-smaller"
                        >
                          {{ item.subtitle }}
                        </span>
                      </v-list-item-subtitle>
                    </div>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <div class="v-select__selection">
                    {{ item ? `${item.name}` : '' }}
                  </div>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-col>

        <v-col
          cols="12"
          class="mb-0 mb-md-6"
          v-if="playlistData.access_type === 'anyone_with_password'"
        >
          <v-row no-gutters align="center" class="pa-0 pa-md-2">
            <v-col
              cols="12"
              md="6"
              class="d-flex flex-column"
              style="align-self: start"
            >
              <span class="body-regular-small title-text-color mb-2 mb-md-0">{{
                $t(
                  'message.playlist.settings.tabs.general.playlistPassword.title'
                )
              }}</span>
              <span
                class="body-regular-smaller desc-text-color d-none d-md-block"
                >{{
                  $t(
                    'message.playlist.settings.tabs.general.playlistPassword.desc'
                  )
                }}</span
              >
            </v-col>
            <v-col cols="12" md="6" style="position: relative">
              <v-input
                ref="passwordInput"
                v-model="password"
                :type="showPass ? 'text' : 'password'"
                :placeholder="
                  hasPassword
                    ? $t(
                        'message.playlist.settings.tabs.general.playlistPassword.placeholderDisabled'
                      )
                    : $t('message.playlist.share.password.placeholder')
                "
                class="access-select playlist-password"
                counter="40"
                max="40"
                :hide-details="hasPassword"
                :disabled="hasPassword"
                :rules="!hasPassword && [rules.required]"
              >
                <template #append>
                  <v-icon
                    v-show="!hasPassword"
                    size="16"
                    @click="showPass = !showPass"
                  >
                    {{
                      !showPass ? 'panda-eye-appearing' : 'panda-eye-hidden'
                    }}</v-icon
                  >
                </template>
              </v-input>
              <a
                v-if="hasPassword"
                type="primary"
                class="body-bold-small cursor-pointer"
                @click="handlePasswordReset"
                >{{
                  $t(
                    'message.playlist.settings.tabs.general.playlistPassword.resetPassword'
                  )
                }}</a
              >
            </v-col>
          </v-row>
        </v-col>
        <portal to="actions-footer-general">
          <v-col cols="12" class="pa-0">
            <div class="d-flex justify-end">
              <base-btn
                type="tertiary"
                class="me-3"
                :disabled="!hasChanges || loadings.saveChanges"
                :width="isMobileBreakpoint ? '50%' : ''"
                :tooltip="!hasChanges ? $t('message.noChanges') : ''"
                @click="handleCancel"
                tooltipMargin="me-3"
              >
                {{ $t('message.actions.cancel') }}
              </base-btn>
              <base-btn
                :disabled="!hasChanges || loadings.saveChanges || !valid"
                :loading="loadings.saveChanges"
                :tooltip="
                  !hasChanges
                    ? $t('message.noChanges')
                    : !valid &&
                      $t('message.playlist.modal.edit.reviewToEditPlaylist')
                "
                @click="handleSaveChanges"
                :width="isMobileBreakpoint ? '50%' : ''"
                keep-width-on-loading
              >
                {{ $t('message.actions.publish') }}
              </base-btn>
            </div>
          </v-col>
        </portal>
      </v-form>
    </v-col>
  </v-row>
</template>
<script>
import i18n from '@/translations/lang'
import { mapState, mapMutations } from 'vuex'
import _ from 'lodash'
import playlistApi from '@/services/playlistApi'

export default {
  name: 'GeneralTab',
  components: {
    BaseTextarea: () => import('@/components/BaseComponents/BaseTextarea.vue'),
    BaseInput: () => import('@/components/BaseComponents/BaseInput.vue'),
  },
  data: () => ({
    i18nPath: 'message.playlist.settings.tabs.general.',
    playlistData: {
      name: '',
      description: '',
      access_type: '',
    },
    initialPlaylistData: {},
    password: '',
    loadings: {
      saveChanges: false,
    },
    showPass: false,
    hasPassword: false,
    valid: true,
    rules: {
      required: (v) => (v && !!v) || i18n.t('message.requiredField'),
      maxLength: (v) =>
        !v.length ||
        v.length <= 250 ||
        i18n.t('message.playlist.modal.rules.maxLengthTitle'),
      minLength: (v) =>
        !v.length ||
        v.length > 2 ||
        i18n.t('message.playlist.modal.rules.minLengthTitle'),
      maxLengthDescription: (v) =>
        !v.length ||
        v.length <= 5000 ||
        i18n.t('message.playlist.modal.rules.maxLengthDesc'),
    },
  }),
  mounted() {
    this.$root.$on('updatePlaylistAccessType', (type) => {
      this.playlistData.access_type = type
      this.initialPlaylistData.access_type = type
    })
    this.handlePlaylistData()
  },
  beforeDestroy() {
    this.$root.$off('updatePlaylistAccessType')
  },
  computed: {
    ...mapState({
      playlist: (state) => state.playlist.playlist,
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    hasChanges() {
      return (
        !_.isEqual(this.playlistData, this.initialPlaylistData) || this.password
      )
    },
    accessTypeItems() {
      return [
        {
          name: i18n.t(
            this.i18nPath + 'playlistAccess.selectOptions.anyoneWithPassword'
          ),
          subtitle: i18n.t(
            this.i18nPath +
              'playlistAccess.selectOptions.anyoneWithPasswordDesc'
          ),
          value: 'anyone_with_password',
        },
        {
          name: i18n.t(
            this.i18nPath + 'playlistAccess.selectOptions.anyoneWithLink'
          ),
          subtitle: i18n.t(
            this.i18nPath + 'playlistAccess.selectOptions.anyoneWithLinkDesc'
          ),
          value: 'anyone_with_link',
        },
      ]
    },
  },
  methods: {
    ...mapMutations('playlist', ['UPDATE_PLAYLIST']),
    handlePlaylistData() {
      this.playlistData.name = this.playlist.name
      this.playlistData.description = this.playlist.description
      this.playlistData.access_type = this.playlist.access_type
      this.hasPassword = this.playlist.access_type === 'anyone_with_password'
      this.initialPlaylistData = _.cloneDeep(this.playlistData)
    },
    handlePasswordReset() {
      this.hasPassword = false
      this.password = ''
      this.hasChanges = true
    },
    async handleSaveChanges() {
      this.loadings.saveChanges = true
      if (this.password) {
        this.playlistData.password = this.password
      }
      try {
        const { data } = await playlistApi.updatePlaylist(
          this.playlistData,
          this.playlist.id
        )
        if (data) {
          this.UPDATE_PLAYLIST(data)
        }
        this.handlePlaylistData()
        this.showSuccessSnackbarAlert()
      } catch (error) {
        console.error('Error saving changes: ', error)
        this.showErrorSnackbarAlert(
          error?.response?.data?.errMsg ?? error?.response?.status
        )
      }
      this.loadings.saveChanges = false
    },
    handleCancel() {
      this.hasPassword =
        this.initialPlaylistData.access_type === 'anyone_with_password'
      this.password = ''
      this.playlistData = _.cloneDeep(this.initialPlaylistData)
    },
    showSuccessSnackbarAlert() {
      this.$store.commit('user/UPDATE_GLOBAL_SNACKBAR', {
        open: true,
        text: this.$t('message.pages.video.changesSaved'),
        type: 'success',
      })
    },
    showErrorSnackbarAlert(errMsg = '') {
      this.$store.commit('user/UPDATE_GLOBAL_SNACKBAR', {
        open: true,
        text: `${this.$t('message.error')}: ${errMsg}`,
        type: 'error',
      })
    },
    handlePasswordInputFocus(val) {
      if (val === 'anyone_with_password') {
        this.$nextTick(() => {
          this.$refs.passwordInput.$el.querySelector('input').focus()
        })
      }
    },
  },
}
</script>

<style lang="scss">
.mobilePadding {
  padding-bottom: 80px;
}

.playlist-password.v-input--is-disabled {
  .v-input__control .v-input__slot .v-text-field__slot input {
    align-self: end;
  }
}

.mobile-margin-bottom {
  margin-bottom: -20px !important;
}

html .v-input.access-select {
  .v-input__append-inner .v-icon {
    margin-top: 2px !important;
  }
  &.filled {
    .v-input__append-inner .v-icon {
      color: varFigma(neutral-60) !important;
    }

    &:hover {
      .v-input__append-inner .v-icon {
        color: varFigma(brand-60-base) !important;
      }
    }
  }
  &.v-input--is-focused {
    &.v-input--is-dirty {
      .v-input__append-inner .v-icon {
        color: varFigma(brand-60-base) !important;
      }
    }
    .v-input__append-inner .v-icon {
      color: varFigma(brand-60-base) !important;
    }
  }
}
html .base-select.v-input.access-select {
  &.filled {
    .v-input__icon--append i {
      color: varFigma(neutral-60) !important;
    }

    &:hover {
      .v-input__icon--append i {
        color: varFigma(brand-60-base) !important;
      }
    }
  }
  &.v-input--is-focused {
    &.v-input--is-dirty {
      .v-input__icon--append i {
        color: varFigma(brand-60-base) !important;
      }
    }
    .v-input__icon--append i {
      color: varFigma(brand-60-base) !important;
    }
  }
}
.access-select-menu {
  max-width: 280px;

  .access-options-list {
    padding: 12px 16px !important;
    min-height: unset;

    .v-list-item__title {
      align-self: auto;
    }
    .v-list-item__subtitle {
      white-space: normal;
    }
    .v-list-item--active {
      background-color: varFigma(brand-95-lighter) !important;
      color: varFigma(brand-40-dark) !important;
    }
  }
  .v-select-list {
    padding: 0 !important;
  }
}

.title-text-color {
  color: varFigma(neutral-20-dark);
}
.desc-text-color {
  color: varFigma(neutral-40-light);
}
.access-option-title-text-color {
  color: varFigma(neutral-30-base);
}
.access-option-subtitle-text-color {
  color: varFigma(neutral-50);
}
</style>
